import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, Loader2, Mail, Download, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import usePageTitle from "@/hooks/usePageTitle";
import { toast } from "@/components/ui/use-toast";

const GiftCardSuccess = () => {
  usePageTitle("Paiement confirmé | Bio Beauté Zen");
  
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [voucher, setVoucher] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [pdfGenerationStatus, setPdfGenerationStatus] = useState<'checking' | 'ready' | 'timeout'>('checking');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const fetchVoucher = async () => {
      if (!sessionId) {
        setIsLoading(false);
        return;
      }
      
      // Tentatives multiples avec délais progressifs
      const maxAttempts = 5;
      const delays = [2000, 3000, 5000, 8000, 10000]; // 2s, 3s, 5s, 8s, 10s
      
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, delays[attempt]));
        
        const { data, error } = await supabase
          .from("gift_vouchers")
          .select("*")
          .eq("stripe_session_id", sessionId)
          .maybeSingle();
        
        if (!error && data) {
          setVoucher(data);
          setIsLoading(false);
          return;
        }
        
        if (attempt === maxAttempts - 1) {
          console.error("Impossible de récupérer le voucher après", maxAttempts, "tentatives");
        }
      }
      
      setIsLoading(false);
    };
    
    fetchVoucher();
  }, [sessionId]);

  // Fonction pour vérifier la disponibilité du PDF
  const checkPdfAvailability = async () => {
    if (!voucher?.voucher_code) return false;
    
    const fileName = `${voucher.voucher_code}.pdf`;
    
    try {
      const { error } = await supabase.storage
        .from("gift-cards-pdfs")
        .download(fileName);
      
      // Si pas d'erreur, le fichier existe
      return !error;
    } catch (error) {
      console.log('[PDF-CHECK] Fichier pas encore disponible:', error);
      return false;
    }
  };

  // Système de polling pour vérifier la disponibilité du PDF
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    const checkPdf = async () => {
      if (!voucher?.voucher_code) return;

      console.log('[PDF-CHECK] Démarrage de la vérification pour:', voucher.voucher_code);

      // Vérifier immédiatement
      const pdfExists = await checkPdfAvailability();
      if (pdfExists) {
        console.log('[PDF-CHECK] PDF trouvé immédiatement !');
        setPdfUploaded(true);
        setShowDownloadButton(true);
        setPdfGenerationStatus('ready');
        return;
      }

      // Si pas encore disponible, lancer le polling
      let attempts = 0;
      const maxAttempts = 10; // 10 tentatives x 3 secondes = 30 secondes max
      
      interval = setInterval(async () => {
        attempts++;
        console.log(`[PDF-CHECK] Tentative ${attempts}/${maxAttempts}`);
        
        const exists = await checkPdfAvailability();
        
        if (exists) {
          console.log('[PDF-CHECK] PDF trouvé !');
          setPdfUploaded(true);
          setShowDownloadButton(true);
          setPdfGenerationStatus('ready');
          if (interval) clearInterval(interval);
        } else if (attempts >= maxAttempts) {
          console.log('[PDF-CHECK] Timeout - PDF non trouvé après 30 secondes');
          setPdfGenerationStatus('timeout');
          if (interval) clearInterval(interval);
        }
      }, 3000);
    };

    checkPdf();

    // Cleanup : s'assurer que l'interval est bien nettoyé
    return () => {
      if (interval) {
        console.log('[PDF-CHECK] Nettoyage du polling');
        clearInterval(interval);
      }
    };
  }, [voucher?.voucher_code]);

  // Gérer le téléchargement manuel du PDF
  const handleDownloadPdf = async () => {
    if (!voucher) return;

    setIsDownloading(true);
    try {
      console.log("Downloading PDF from storage for voucher:", voucher.voucher_code);
      
      // Télécharger le PDF depuis Storage
      const fileName = `${voucher.voucher_code}.pdf`;
      const { data, error } = await supabase.storage
        .from("gift-cards-pdfs")
        .download(fileName);

      if (error) {
        console.error("Error downloading PDF:", error);
        toast({
          title: "Erreur",
          description: "Impossible de télécharger le PDF",
          variant: "destructive"
        });
        return;
      }

      // Créer un lien de téléchargement
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `BonCadeau-${voucher.voucher_code}.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      toast({
        title: "Téléchargement réussi",
        description: "Le bon cadeau a été téléchargé"
      });
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors du téléchargement",
        variant: "destructive"
      });
    } finally {
      setIsDownloading(false);
    }
  };
  
  if (isLoading) {
    return (
      <main className="pt-24">
        <div className="container mx-auto px-4 flex flex-col items-center justify-center min-h-[50vh]">
          <Loader2 className="h-12 w-12 animate-spin text-bbz-green mb-4" />
          <p className="text-lg">Vérification de votre paiement...</p>
          <p className="text-sm text-bbz-text/70 mt-2">Cela peut prendre quelques instants.</p>
        </div>
      </main>
    );
  }
  
  return (
    <main className="pt-24 pb-16">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="font-playfair text-3xl font-semibold text-bbz-text mb-2">
              Paiement confirmé !
            </h1>
            <p className="text-lg text-bbz-text/70">
              Votre bon cadeau a été créé avec succès
            </p>
          </div>
          
          {voucher ? (
            <div className="bg-bbz-green/10 p-6 rounded-lg mb-6">
              <p className="font-medium mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Un email vous a été envoyé
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>
                    <strong>Conseil :</strong> Si vous ne recevez pas l'email dans les 5 minutes, pensez à vérifier votre dossier spam ou courrier indésirable.
                  </span>
                </p>
              </div>
              
              <p className="text-sm text-bbz-text/70 mb-2">
                Le bon cadeau a été envoyé par email à :
              </p>
              <p className="font-medium mb-4">{voucher.email}</p>
              
              <div className="mt-4 pt-4 border-t border-bbz-green/20">
                <p className="text-sm mb-2"><strong>Code du bon cadeau :</strong> {voucher.voucher_code}</p>
                <p className="text-sm mb-2"><strong>Bénéficiaire :</strong> {voucher.recipient_name}</p>
                <p className="text-sm mb-2"><strong>Date d'expiration :</strong> {new Date(voucher.expiry_date).toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric"
                })}</p>
                {voucher.amount && (
                  <p className="text-sm"><strong>Montant :</strong> {voucher.amount}€</p>
                )}
              </div>

              {showDownloadButton && (
                <div className="mt-4 pt-4 border-t border-bbz-green/20">
                  <p className="text-sm text-bbz-text/70 mb-3">
                    Vous pouvez également télécharger le bon cadeau :
                  </p>
                  <Button
                    onClick={handleDownloadPdf}
                    className="w-full md:w-auto"
                    variant="outline"
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Téléchargement...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Télécharger le PDF
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg mb-6">
              <p className="text-amber-800 mb-2 font-semibold">
                Votre paiement a été confirmé !
              </p>
              <div className="space-y-3">
                <p className="text-sm text-amber-700">
                  {pdfGenerationStatus === 'checking' && (
                    <>
                      Nous finalisons la création de votre bon cadeau
                      <span className="inline-block animate-pulse ml-2">⏳</span>
                    </>
                  )}
                  {pdfGenerationStatus === 'ready' && (
                    <>Votre bon cadeau est prêt ! 🎉</>
                  )}
                  {pdfGenerationStatus === 'timeout' && (
                    <>La génération du PDF prend plus de temps que prévu...</>
                  )}
                </p>
                <p className="text-sm text-amber-700">
                  Vous recevrez un email avec le PDF dans quelques instants.
                </p>
                {pdfGenerationStatus === 'timeout' && (
                  <p className="text-xs text-amber-600">
                    Si vous ne recevez pas l'email sous 10 minutes, vérifiez vos spams ou contactez-nous avec ce code de session : <code className="bg-amber-100 px-2 py-1 rounded">{sessionId?.substring(0, 20)}...</code>
                  </p>
                )}
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div className="bg-bbz-beige/30 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Que faire ensuite ?</h3>
              <ul className="text-sm space-y-1 list-disc list-inside text-bbz-text/70">
                <li>Consultez votre boîte email pour le bon cadeau PDF</li>
                <li>Vous pouvez également le télécharger depuis cette page</li>
                <li>Imprimez-le ou transférez-le au bénéficiaire</li>
                <li>Le bénéficiaire devra prendre rendez-vous en mentionnant le code</li>
              </ul>
            </div>
            
            <div className="text-center">
              <Button asChild className="w-full md:w-auto">
                <a href="/">Retour à l'accueil</a>
              </Button>
            </div>
          </div>
        </div>
      </div>

    </main>
  );
};

export default GiftCardSuccess;
